import React, { useState } from "react";
import { User } from "../types/user.types";

interface UserDetailsProps {
  user?: User;
  onSave?: (userData: Partial<User>) => Promise<void>;
  isEdit?: boolean;
  // Only SUPERADMIN can edit groups
  canEditGroups?: boolean;
}

const UserDetails: React.FC<UserDetailsProps> = ({
  user,
  onSave,
  isEdit = false,
  canEditGroups = false,
}) => {
  const [isEditing, setIsEditing] = useState(isEdit);
  const [formData, setFormData] = useState<Partial<User>>(
    user || {
      display_name: "",
      email: "",
      status: "ACTIVE",
      groups: "Customer", // Changed from array to string
      subscription_tier: "DAISY_LIGHT",
    }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGroupChange = (group: string) => {
    setFormData((prev) => ({
      ...prev,
      groups: group, // Simply set the selected group
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (onSave) {
        await onSave(formData);
      }
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-semibold text-gray-900">
            {user ? "User Details" : "Add New User"}
          </h3>
          {user && user.groups === "SuperAdmin" && (
            <span
              title="Superadmin"
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#eaf3ef] text-[#2f4941] border border-[#2f4941]"
            >
              Superadmin
            </span>
          )}
        </div>
        {user && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-[#2f4941] text-white rounded-md hover:bg-[#1e3128] transition-colors"
          >
            Edit User
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      )}

      {isEditing || !user ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {user?.user_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User ID
              </label>
              <input
                type="text"
                value={user.user_id}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <input
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2f4941]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2f4941]"
              required
            />
          </div>

          {formData.email && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (lowercase)
              </label>
              <input
                type="text"
                value={formData.email.toLowerCase()}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2f4941]"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>

          {user &&
            (canEditGroups ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groups
                </label>
                <div className="space-y-2">
                  {["Internal", "SuperAdmin", "Customer"].map((group) => (
                    <label key={group} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.groups === group}
                        onChange={() => handleGroupChange(group)}
                        className="h-4 w-4 text-[#2f4941] rounded border-gray-300 focus:ring-[#2f4941]"
                        name="groups"
                      />
                      <span className="text-sm text-gray-700">{group}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groups
                </label>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#eaf3ef] text-[#2f4941] border border-[#2f4941]">
                    {user.groups}
                  </span>
                </div>
              </div>
            ))}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Tier
            </label>
            <select
              name="subscription_tier"
              value={formData.subscription_tier}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2f4941]"
            >
              <option value="DAISY_LIGHT">Daisy — Light</option>
              <option value="DAISY_PRO">Daisy — Pro</option>
              <option value="DAISY_SCALE">Daisy — Scale</option>
              <option disabled>──────────</option>
              <option value="ROSE_LIGHT">Rose — Light</option>
              <option value="ROSE_PRO">Rose — Pro</option>
              <option value="ROSE_SCALE">Rose — Scale</option>
            </select>
            <div className="mt-2 text-xs text-gray-500 space-y-0.5">
              <p>
                <span className="font-medium text-[#2f4941]">Daisy</span> — AI
                social media manager
              </p>
              <p>
                <span className="font-medium text-[#2f4941]">Rose</span> — Voice
                AI
              </p>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#2f4941] text-white rounded-md hover:bg-[#1e3128] transition-colors disabled:bg-gray-400"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(user || {});
                }}
                className="flex-1 px-4 py-2 border border-[#2f4941] text-[#2f4941] rounded-md hover:bg-[#eaf3ef] transition-colors"
              >
                Cancel
              </button>
            )}
          </div>

          {/* Fine Print */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              ©2025 DaisyAI, LLC. All rights reserved.{" "}
              <a
                href="https://www.daisymarketingteam.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2f4941] hover:underline"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://www.daisymarketingteam.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2f4941] hover:underline"
              >
                Terms of Service
              </a>
              . Contact us at{" "}
              <a
                href="mailto:info@daisy-ai.com"
                className="text-[#2f4941] hover:underline"
              >
                info@daisy-ai.com
              </a>{" "}
              for support.
            </p>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">User ID</p>
              <p className="font-medium break-all">{user.user_id}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Display Name</p>
              <p className="font-medium">{user.display_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{user.email}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Email (lowercase)</p>
              <p className="font-medium">{user.email.toLowerCase()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-medium">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${
                    user.status === "ACTIVE"
                      ? "bg-green-100 text-green-800"
                      : user.status === "INACTIVE"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {user.status}
                </span>
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Groups</p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#eaf3ef] text-[#2f4941] border border-[#2f4941]">
                  {user.groups}
                </span>
              </div>
            </div>
          </div>

          {user.subscription_tier && (
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Subscription Tier</p>
              <p className="font-medium">
                {(() => {
                  const map: Record<string, string> = {
                    DAISY_LIGHT: "Daisy — Light",
                    DAISY_PRO: "Daisy — Pro",
                    DAISY_SCALE: "Daisy — Scale",
                    ROSE_LIGHT: "Rose — Light",
                    ROSE_PRO: "Rose — Pro",
                    ROSE_SCALE: "Rose — Scale",
                  };
                  return map[user.subscription_tier!] || user.subscription_tier;
                })()}
              </p>
              <p className="text-xs text-gray-500">
                {(() => {
                  const suite = (user.subscription_tier || "").startsWith(
                    "ROSE"
                  )
                    ? "Rose"
                    : "Daisy";
                  return suite === "Rose"
                    ? "Voice AI"
                    : "AI social media manager";
                })()}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-medium">
                {new Date(user.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
